/* The log-in screen, following Contributor_WebApp_v2.16's `RENDER.login`.
 *
 * The structure is the prototype's, in order: "Welcome back" eyebrow, the
 * "Log in" title, the lead line, email over password with a Show/Hide toggle
 * inside the password field, and the full-width "Log in →" button.
 *
 * 27 OCT 2026 — MANAGER'S REVIEW, REMOVED: the "Shaip Hub · Egocentric
 * annotation" title above the card; the "crowd" / "Your password" placeholders
 * (they read as if "crowd" were already typed in); "Forgot password?"; the
 * "or" divider, Continue with Google, the one-time-code and Create-an-account
 * lines (none of them works in this build); and the demo usernames under the
 * card. The prototype had all of these; they were placeholders that only
 * showed a "not in this build" toast.
 *
 * TWO DEVIATIONS FROM THE PROTOTYPE'S CONTENT
 *   * The field is "Email or username": the prototype validates a real email
 *     address, and the demo account is a plain username (user0108). A field
 *     labelled Email that rejects a valid username is the worst of both.
 *   * The lead line names this tool rather than "explore jobs", which is the
 *     contributor app's job board and does not exist here.
 *
 * What this is NOT is in lib/auth.ts: the check happens in the browser and
 * protects nothing. It is a front door, not security.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Eyebrow } from "../components/ui";
import { isSignedIn, signIn } from "../lib/auth";

export function Login() {
  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const identityRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Where the guard sent us from, so logging in resumes the click that was
  // interrupted rather than always landing on the queue.
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  /* NO AUTO-FOCUS ON THE USERNAME FIELD.
   *
   * Testing: "when launch the Login screen the saved credentials pop up is
   * always open." That popup is Chrome's account chooser, and focusing a field
   * with autoComplete="username" is precisely what summons it — so the app
   * opened with a browser overlay covering its own form, every single time, and
   * the first click went to dismissing it.
   *
   * The field is the first thing on the form and a click or Tab reaches it. The
   * autocomplete attributes stay: a password manager that fills on request is
   * useful, one that takes over the screen on arrival is not. */
  useEffect(() => {
    if (isSignedIn()) navigate(from, { replace: true });
  }, [from, navigate]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const message = signIn(identity, password);
    setBusy(false);
    if (message) {
      setError(message);
      setPassword("");      // a wrong attempt should not be resubmittable by Enter
      return;
    }
    navigate(from, { replace: true });
  }

  const field = "w-full rounded-field border border-line bg-card px-3 py-2.5 text-sm " +
                "text-ink placeholder:text-faint";

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto px-6 py-10">
      <div className="w-full max-w-sm">
        <form onSubmit={submit}
          className="rounded-modal border border-line bg-card p-6 shadow-card">

          <div><Eyebrow>Welcome back</Eyebrow></div>
          <h1 className="mt-1 text-xl font-extrabold tracking-[-0.01em] text-ink">Log in</h1>
          <p className="mt-1.5 text-[13px] leading-relaxed text-text2">
            Log in to pick up where you left off — your assigned videos are
            waiting in your queue.
          </p>

          <label className="mt-5 block">
            <span className="mb-1 block text-[12.5px] font-medium text-text2">
              Email or username
            </span>
            <input
              ref={identityRef}
              value={identity}
              onChange={(e) => { setIdentity(e.target.value); setError(null); }}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              className={field}
            />
          </label>

          <label className="mt-4 block">
            <span className="mb-1 block text-[12.5px] font-medium text-text2">
              Password
            </span>
            {/* Show/hide inside the field, as the prototype has it. Genuinely
                useful on a demo laptop where the password was said out loud. */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                autoComplete="current-password"
                className={`${field} pr-16`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[12.5px]
                  text-teal hover:text-teal7"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          {error && (
            <p role="alert" className="mt-4 rounded-field border
              border-verdict-rejected-line bg-verdict-rejected-bg px-3 py-2
              text-[12.5px] text-verdict-rejected-ink">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" disabled={busy}
            className="mt-4 w-full py-2.5">
            {busy ? "Logging in…" : "Log in  →"}
          </Button>
        </form>
      </div>
    </div>
  );
}
